import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { usePinnedEmployees } from 'components/hooks/quotes/usePinnedEmployees'
import { useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import { useGetQuotingQuoteResultsQuoteIdPlanAvailability as useGetPlanAvailability } from 'utils/web_api'
import { type QuotingEmployee, type QuotingMedicalPlan, type QuotingPlanAvailability } from 'utils/web_api.schemas'

type QuotingEmployeeProviderProps = {
  employees: QuotingEmployee[]
  monthlyAllowanceForEmployee: (employee: QuotingEmployee) => number
  monthlyEmployeeOutOfPocketContribution: (employee: QuotingEmployee) => number
  isAllowanceAffordable?: (employee: QuotingEmployee) => boolean
  minAffordableAllowanceForEmployee?: (employee: QuotingEmployee) => number | undefined
  medicalPlanForEmployee?: (employee: QuotingEmployee) => QuotingMedicalPlan | undefined
  isEmployeeInProjectedState?: (employee: QuotingEmployee) => boolean
}

type QuotingEmployeeContextType = QuotingEmployeeProviderProps & {
  selectedEmployee?: QuotingEmployee
  onEmployeeSelected: (employee?: QuotingEmployee) => void
  pinnedEmployeeIds: string[]
  onPinnedEmployeeIdsChange: (employeeIds: string[]) => void
  handleEmployeePinToggle: (employeeId: string) => void
  isEmployeePinned: (employeeId: string) => boolean
  filterAndSortEmployees: (employees: QuotingEmployee[], searchQuery?: string, additionalFilters?: (employee: QuotingEmployee) => boolean) => QuotingEmployee[]
  createPinnedSortingFn: (getSortingDirection: (columnId: string) => 'asc' | 'desc' | false) => (rowA: { original: QuotingEmployee }, rowB: { original: QuotingEmployee }, columnId: string) => number
  willEnrollEmployeeIds: Set<string>
  onWillEnrollEmployeeIdsChange: (employeeIds: Set<string>) => void
  getPlanAvailabilityForEmployee: (employee: QuotingEmployee) => QuotingPlanAvailability | undefined
}

const QuotingEmployeeContext = createContext<QuotingEmployeeContextType>({
  employees: [],
  onEmployeeSelected: () => {},
  pinnedEmployeeIds: [],
  onPinnedEmployeeIdsChange: () => {},
  handleEmployeePinToggle: () => {},
  isEmployeePinned: () => false,
  filterAndSortEmployees: () => [],
  createPinnedSortingFn: () => () => 0,
  monthlyAllowanceForEmployee: () => 0,
  monthlyEmployeeOutOfPocketContribution: () => 0,
  willEnrollEmployeeIds: new Set(),
  onWillEnrollEmployeeIdsChange: () => {},
  getPlanAvailabilityForEmployee: () => undefined,
})

export function useQuotingEmployeeContext () {
  return useContext(QuotingEmployeeContext)
}

export function QuotingEmployeeProvider ({
  employees,
  monthlyAllowanceForEmployee,
  monthlyEmployeeOutOfPocketContribution,
  isAllowanceAffordable,
  minAffordableAllowanceForEmployee,
  medicalPlanForEmployee,
  isEmployeeInProjectedState,
  children,
}: PropsWithChildren<QuotingEmployeeProviderProps>) {
  const { quoteId } = useQuotePricing()

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>()
  const [willEnrollEmployeeIds, setWillEnrollEmployeeIds] = useState<Set<string>>(new Set())

  const {
    pinnedEmployeeIds,
    setPinnedEmployeeIds,
    handleEmployeePinToggle,
    isEmployeePinned,
    filterAndSortEmployees,
    createPinnedSortingFn
  } = usePinnedEmployees<QuotingEmployee>()

  const { data: planAvailability } = useGetPlanAvailability<QuotingPlanAvailability[]>(
    quoteId,
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: true,
      },
    }
  )

  const getPlanAvailabilityForEmployee = useCallback((employee: QuotingEmployee) => {
    return planAvailability?.find((availability: QuotingPlanAvailability) => availability.rating_area_code === employee.rating_area_code)
  }, [planAvailability])

  useEffect(() => {
    setWillEnrollEmployeeIds(new Set(employees.map((employee) => employee.id)))
  }, [employees])

  const employeesById = useMemo(() => {
    return employees.reduce((acc, employee) => {
      acc[employee.id] = employee
      return acc
    }, {} as Record<string, QuotingEmployee>)
  }, [employees])

  const selectedEmployee = useMemo(() => {
    return selectedEmployeeId ? employeesById[selectedEmployeeId] : undefined
  }, [employeesById, selectedEmployeeId])

  const onEmployeeSelected = useCallback((employee?: QuotingEmployee) => {
    if (employee?.id === selectedEmployeeId) {
      setSelectedEmployeeId(undefined)
    } else {
      setSelectedEmployeeId(employee?.id)
    }
  }, [selectedEmployeeId])

  const onWillEnrollEmployeeIdsChange = useCallback((employeeIds: Set<string>) => {
    setWillEnrollEmployeeIds(employeeIds)
  }, [])

  return (
    <QuotingEmployeeContext.Provider
      value={{
        employees,
        selectedEmployee,
        onEmployeeSelected,
        pinnedEmployeeIds,
        onPinnedEmployeeIdsChange: setPinnedEmployeeIds,
        handleEmployeePinToggle,
        isEmployeePinned,
        filterAndSortEmployees,
        createPinnedSortingFn,
        monthlyAllowanceForEmployee,
        monthlyEmployeeOutOfPocketContribution,
        isAllowanceAffordable,
        minAffordableAllowanceForEmployee,
        medicalPlanForEmployee,
        isEmployeeInProjectedState,
        willEnrollEmployeeIds,
        onWillEnrollEmployeeIdsChange,
        getPlanAvailabilityForEmployee,
      }}
    >
      {children}
    </QuotingEmployeeContext.Provider>
  )
}

export default QuotingEmployeeContext
